import React from 'react';
import { Button } from '@/core/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components';
import { staticUrl } from '@/core/utils';
import { DIALOG_TYPES } from '@/core/types/enums';
import { useTranslation } from 'react-i18next';
import { DialogConfig } from '@/core/types';
import { cn } from '@/core/lib/utils';

interface DialogProps {
  isOpen: boolean;
  onClose: (result: unknown) => void;
  config: DialogConfig;
}
/**
 * StartNowDialog - Confirmation dialog for starting upcoming plan immediately
 */
const CustomDialog: React.FC<DialogProps> = ({ isOpen, onClose, config }) => {
  const {
    type,
    title,
    description,
    secondaryButton,
    primaryButton,
    showCloseButton,
    footerClassName,
  } = config;

  const getIconByType = (type: string) => {
    switch (type) {
      case DIALOG_TYPES.SUCCESS:
        return '/icons/featured-success-outline-lg.svg';
      case DIALOG_TYPES.WARNING:
        return '/icons/featured-warning-outline-lg.svg';
      case DIALOG_TYPES.ERROR:
        return '/icons/featured-error-outline-lg.svg';
    }
  };
  const icon = getIconByType(type);
  const { t } = useTranslation();
  return (
    <Dialog
      open={isOpen}
      onOpenChange={() => onClose(false)}
      showCloseButton={showCloseButton}
      data-pte-name='custom-dialog'
    >
      <DialogContent
        className='sm:max-w-[400px]'
        data-pte-name='custom-dialog-content'
      >
        <DialogHeader data-pte-name='custom-dialog-header'>
          <div
            className='rounded-full flex items-start justify-start mb-3'
            data-pte-name='custom-dialog-icon'
          >
            <img
              src={staticUrl(icon || '')}
              alt='Warning'
              width={48}
              height={48}
            />
          </div>

          <DialogTitle className='mb-3' data-pte-name='custom-dialog-title'>
            {title}
          </DialogTitle>

          <DialogDescription
            dangerouslySetInnerHTML={{ __html: description || '' }}
            data-pte-name='custom-dialog-description'
          />
        </DialogHeader>

        <DialogFooter
          className={cn('flex gap-2', footerClassName)}
          data-pte-name='custom-dialog-footer'
        >
          {(!secondaryButton || secondaryButton?.isShow !== false) && (
            <Button
              intent={
                secondaryButton?.intent ||
                (type === DIALOG_TYPES.ERROR ? 'destructive' : 'cta')
              }
              variant='tertiary'
              onClick={() => onClose(false)}
              className='text-primary-2 flex-1'
              data-pte-name='custom-dialog-secondary-btn'
            >
              {secondaryButton?.text || t('Cancel')}
            </Button>
          )}

          {primaryButton && (
            <Button
              intent={
                primaryButton.intent ||
                (type === DIALOG_TYPES.ERROR ? 'destructive' : 'cta')
              }
              variant='primary'
              className={cn(
                `${type === DIALOG_TYPES.ERROR ? 'bg-error-default' : ''} text-white flex-1`,
                primaryButton.className
              )}
              onClick={() =>
                primaryButton.type ? onClose(primaryButton.type) : onClose(true)
              }
              data-pte-name='custom-dialog-primary-btn'
            >
              {primaryButton.text || t('OK')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDialog;
